/** One conversation (user ↔ admin). Per Final Version — Chat System Documentation. Store both conversationId and roomId from API. */
export interface ChatConversation {
  /** Backend sends conversationId; _id is fallback for compatibility. Use for REST :id routes and POST body. */
  conversationId?: string
  _id?: string
  /** Unique room string from API (e.g. room_<id1>_<id2>_<uuid>). Use for join-room and optional chat:send; never derive or guess. */
  roomId?: string
  /** "active" = current thread; "archived" = old (e.g. after start-new). Default "active". */
  status?: 'active' | 'archived'
  /** Set when conversation is archived. */
  archivedAt?: string
  participants: Array<{ _id: string; firstName?: string; lastName?: string }>
  lastMessage?: {
    _id: string
    content: string
    createdAt: string
    senderId: string
    read?: boolean
  }
  unreadCount?: number
  updatedAt?: string
  /** True when this conversation is the user ↔ AI bot thread (GET /api/chat/conversations returns it). Use for send: POST /api/chat/ai/message vs POST /api/chat/messages. */
  isAiConversation?: boolean
}

/** Get conversation ID as string: conversationId (preferred) or _id (fallback). Normalized so comparison and API body are reliable. */
export function getConversationId(conv: ChatConversation | null | undefined): string | null {
  if (!conv) return null
  const raw = conv.conversationId ?? conv._id ?? null
  return raw != null ? String(raw) : null
}

/** Get roomId from conversation (use for join-room, leave, optional send). Never derive — only from API. */
export function getRoomId(conv: ChatConversation | null | undefined): string | null {
  if (!conv?.roomId) return null
  return conv.roomId
}

/** Message type for UI display */
export type MessageType = 'user' | 'admin' | 'ai' | 'system'

/** One message in a conversation. Per Final Version — Chat System Documentation. */
export interface ChatMessage {
  _id: string
  /** Required — conversation this message belongs to. Every message (REST and Socket) includes this. */
  conversationId?: string
  /** Backend room identifier; payloads may include it. Frontend keys state by conversationId only. */
  roomId?: string
  /** Populated object in Socket.IO and REST; UI uses senderId._id for isMe. */
  senderId: string | { _id: string; firstName?: string; lastName?: string; email?: string; avatar?: string }
  /** Present in Socket.IO payload; optional in REST. */
  receiverId?: string | { _id: string; firstName?: string; lastName?: string }
  content: string
  read?: boolean
  createdAt: string
  /** Backend field: true for AI-generated messages */
  isAI?: boolean
  /** Frontend display fields. */
  isMe?: boolean
  senderName?: string
  avatar?: string
  /** Frontend computed: message type for UI styling */
  messageType?: MessageType
  /** AI confidence score (0-1) for AI messages */
  confidence?: number
  /** AI response source: 'faq' or 'ai' */
  source?: 'faq' | 'ai'
}
